/**
 * Which apps the authorise page links, and with which key.
 *
 * `APP_FOLDER` is the apps confined to a folder of their own, and what that
 * folder is called. `VAULT_APP` is the two given the master key: the hub, which
 * browses the whole vault, and — since 2026-09-11 — Relay, which browses Cloud
 * and attaches from it, which a device confined to its own folder cannot do.
 *
 * Three lists have to agree: these, `SCOPED_CLIENTS` in `worker/lib/scope.ts`,
 * and `VAULT_SCOPE_CLIENTS` in `worker/routes/devices.ts`. An app scoped there
 * but missing here would be linked with the master key, and one missing there
 * but present here would be handed a folder key it is not confined to — either
 * way a key that opens the wrong thing, which is the failure that looks like
 * success.
 *
 * `appscope.test.ts` holds the three against each other, because they have
 * already come apart once: `relay` moved in both Worker lists and in this
 * page's comment, not in its code, and the page answered "That app is not one
 * of ours" to the one app that had just been given the vault.
 */

export const APP_FOLDER: Readonly<Record<string, string>> = {
  snap: 'Snap',
  compose: 'Compose',
  mail: 'Mail'
};

export const VAULT_APP: Readonly<Record<string, string>> = {
  suite: 'Suite',
  relay: 'Relay'
};

/**
 * The key this page hands `client`: a fresh folder key, the master key, or
 * nothing at all for a client it does not know.
 *
 * Both answers are named rather than defaulted. Falling through to 'vault' for
 * an unrecognised client would mean a fragment saying `client=anything` walked
 * away with the whole vault — the permissive answer arrived at by omission,
 * which is the shape of this bug everywhere it appears. `hasOwn` rather than a
 * lookup, so `client=constructor` is not found on the prototype.
 */
export function authoriseScope(client: string): 'vault' | 'app' | null {
  if (Object.hasOwn(APP_FOLDER, client)) return 'app';
  if (Object.hasOwn(VAULT_APP, client)) return 'vault';
  return null;
}

/** What the page calls `client` — "Link Relay", not "Link relay". */
export function appName(client: string): string {
  if (Object.hasOwn(APP_FOLDER, client)) return APP_FOLDER[client] ?? client;
  if (Object.hasOwn(VAULT_APP, client)) return VAULT_APP[client] ?? client;
  return client;
}

/**
 * Is this device's area an app's rather than a computer's?
 *
 * Every linked device can have an area, and only the hub's is a machine's
 * synced folders. An app's — Snap's captures, Compose's folder, Relay's — is
 * listed apart from the computers. An unnamed device or a client this page does
 * not know stays a computer, which is how every device was listed before.
 */
export function isAppArea(client: string | null): boolean {
  if (!client || client === 'suite') return false;
  return authoriseScope(client) !== null;
}
